import {Centrifuge, ServerPublicationContext} from 'centrifuge';
import {camelizeKeys} from 'humps';
import {noop} from 'lodash';
import {Dispatch, MutableRefObject, SetStateAction, useEffect} from 'react';

import {isRecord} from '../../../util/object';

import {applyReviewPresencePublication, removeExpiredReviewLocks} from './review-realtime-lock-store';
import {
    REVIEW_PRESENCE_HEARTBEAT_MS,
    REVIEW_PRESENCE_LOCK_TTL_MS,
    REVIEW_PRESENCE_SWEEPER_MS,
} from './review-realtime-presence-const';
import {isBrandPresenceChannel} from './review-realtime-presence-helper';
import {
    ReviewPresenceActivityOptionsType,
    ReviewPresenceEventEnum,
    reviewPresenceLockPublicationSchema,
    reviewPresenceUpdatePublicationSchema,
    ReviewPresenceUpdatePublicationType,
    ReviewRealtimeLockMapType,
} from './review-realtime-presence-type';
import {logReviewPresenceParseFailure} from './review-realtime-update-helper';

type PublishPresenceEventType = (
    options: ReviewPresenceActivityOptionsType & {
        event: ReviewPresenceEventEnum.Lock | ReviewPresenceEventEnum.Unlock | ReviewPresenceEventEnum.Heartbeat;
    }
) => Promise<void>;

type HandleReviewPresencePublicationOptionsType = {
    onReviewUpdate?: (payload: ReviewPresenceUpdatePublicationType) => void;
    publicationContext: ServerPublicationContext;
    sessionIdRef: MutableRefObject<string>;
    setLocksByReviewId: Dispatch<SetStateAction<ReviewRealtimeLockMapType>>;
};

function handleReviewPresencePublication(options: HandleReviewPresencePublicationOptionsType): void {
    const {onReviewUpdate, publicationContext, sessionIdRef, setLocksByReviewId} = options;

    if (!isBrandPresenceChannel(publicationContext.channel)) {
        return;
    }

    const camelizedData = camelizeKeys(publicationContext.data);

    if (!isRecord(camelizedData)) {
        return;
    }

    if (camelizedData.event === ReviewPresenceEventEnum.Update) {
        const updateParseResult = reviewPresenceUpdatePublicationSchema.safeParse(camelizedData);

        if (!updateParseResult.success) {
            logReviewPresenceParseFailure('receive-update', updateParseResult.error);

            return;
        }

        if (updateParseResult.data.sessionId === sessionIdRef.current) {
            return;
        }

        onReviewUpdate?.(updateParseResult.data);

        return;
    }

    const lockParseResult = reviewPresenceLockPublicationSchema.safeParse(camelizedData);

    if (!lockParseResult.success) {
        logReviewPresenceParseFailure('receive-lock', lockParseResult.error);

        return;
    }

    setLocksByReviewId((previousLocks) => applyReviewPresencePublication(previousLocks, lockParseResult.data));
}

type UseReviewRealtimePresenceRuntimeHookOptionsType = {
    activeActivitiesRef: MutableRefObject<Array<ReviewPresenceActivityOptionsType>>;
    centrifuge: Centrifuge | null;
    isFeatureEnabled: boolean;
    onReviewUpdate?: (payload: ReviewPresenceUpdatePublicationType) => void;
    publishPresenceEvent: PublishPresenceEventType;
    sessionIdRef: MutableRefObject<string>;
    setLocksByReviewId: Dispatch<SetStateAction<ReviewRealtimeLockMapType>>;
    stopAllActivities: () => void;
};

export function useReviewRealtimePresenceRuntimeHook(options: UseReviewRealtimePresenceRuntimeHookOptionsType): void {
    const {
        activeActivitiesRef,
        centrifuge,
        isFeatureEnabled,
        onReviewUpdate,
        publishPresenceEvent,
        sessionIdRef,
        setLocksByReviewId,
        stopAllActivities,
    } = options;

    useEffect(() => {
        if (!isFeatureEnabled) {
            return noop;
        }

        return () => {
            stopAllActivities();
        };
    }, [isFeatureEnabled, stopAllActivities]);

    useEffect(() => {
        if (!isFeatureEnabled) {
            return noop;
        }

        function handleBeforeUnload(): void {
            stopAllActivities();
        }

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isFeatureEnabled, stopAllActivities]);

    useEffect(() => {
        if (!isFeatureEnabled) {
            return noop;
        }

        const heartbeatIntervalId = window.setInterval(() => {
            activeActivitiesRef.current.forEach((activity) => {
                publishPresenceEvent({...activity, event: ReviewPresenceEventEnum.Heartbeat}).catch(() => {
                    /* ignore publish errors */
                });
            });
        }, REVIEW_PRESENCE_HEARTBEAT_MS);

        return () => {
            window.clearInterval(heartbeatIntervalId);
        };
    }, [activeActivitiesRef, isFeatureEnabled, publishPresenceEvent]);

    useEffect(() => {
        if (!isFeatureEnabled) {
            return noop;
        }

        const sweeperIntervalId = window.setInterval(() => {
            setLocksByReviewId((previousLocks) =>
                removeExpiredReviewLocks(previousLocks, Date.now(), REVIEW_PRESENCE_LOCK_TTL_MS)
            );
        }, REVIEW_PRESENCE_SWEEPER_MS);

        return () => {
            window.clearInterval(sweeperIntervalId);
        };
    }, [isFeatureEnabled, setLocksByReviewId]);

    useEffect(() => {
        if (!centrifuge || !isFeatureEnabled) {
            return noop;
        }

        function handlePublication(publicationContext: ServerPublicationContext): void {
            handleReviewPresencePublication({
                onReviewUpdate,
                publicationContext,
                sessionIdRef,
                setLocksByReviewId,
            });
        }

        centrifuge.on('publication', handlePublication);

        return () => {
            centrifuge.off('publication', handlePublication);
        };
    }, [centrifuge, isFeatureEnabled, onReviewUpdate, sessionIdRef, setLocksByReviewId]);
}
